/**
 * 将小驼峰的单词转换为下划线后跟小写字母
 * 
 * @param key - 需要转换的字符串
 * @returns 转换后的下划线命名字符串
 * 
 * @example
 * ```ts
 * camelCaseToUnderscore('statusCode'); // => 'status_code'
 * camelCaseToUnderscore('userName'); // => 'user_name'
 * camelCaseToUnderscore('apiKey'); // => 'api_key'
 * ```
 */
function camelCaseToUnderscore(key: string = ''): string {
  return key
    .replace(/([a-z\d])([A-Z])/g, '$1_$2') // 在小写字母/数字和大写字母之间插入下划线
    .replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1_$2') // 处理连续大写字母的情况
    .toLowerCase();
}

/**
 * 递归将所有小驼峰方式的属性名替换为下划线命名
 *
 * 该函数深度遍历对象和数组，将所有属性名从驼峰命名转换为下划线命名。
 * 对于字符串输入，直接进行下划线转换。
 * 常用于向后端 API 发送数据时的格式转换。
 *
 * @param data - 需要转换的数据，可以是对象、数组、字符串或原始值
 * @returns 转换后的数据，保持原始数据结构不变
 *
 * @example
 * ```ts
 * import { convertToUnderscore } from '@fu/matrix';
 *
 * // 字符串转换
 * convertToUnderscore('userName'); // => 'user_name'
 * convertToUnderscore('apiKeySecret'); // => 'api_key_secret'
 *
 * // 对象转换
 * const formData = {
 *   userName: 'John',
 *   emailAddress: 'john@example.com',
 *   createdAt: '2023-01-01'
 * };
 * convertToUnderscore(formData);
 * // => { user_name: 'John', email_address: 'john@example.com', created_at: '2023-01-01' }
 *
 * // 数组转换
 * const userList = [
 *   { userId: 1, userName: 'Alice' },
 *   { userId: 2, userName: 'Bob' }
 * ];
 * convertToUnderscore(userList);
 * // => [{ user_id: 1, user_name: 'Alice' }, { user_id: 2, user_name: 'Bob' }]
 *
 * // 嵌套对象转换
 * const complexData = {
 *   userInfo: {
 *     basicInfo: {
 *       firstName: 'John',
 *       lastName: 'Doe'
 *     },
 *     contactList: [
 *       { phoneNumber: '123-456-7890' }
 *     ]
 *   }
 * };
 * convertToUnderscore(complexData);
 * // => {
 * //   user_info: {
 * //     basic_info: {
 * //       first_name: 'John',
 * //       last_name: 'Doe'
 * //     },
 * //     contact_list: [
 * //       { phone_number: '123-456-7890' }
 * //     ]
 * //   }
 * // }
 * ```
 *
 * @since 1.4.0
 */
export function convertToUnderscore<T = any>(data: T): T {
  const type = Object.prototype.toString.call(data);

  if (type === '[object String]') {
    // 处理字符串 - 直接进行转换
    return camelCaseToUnderscore(data as string) as T;
  } else if (type === '[object Array]') {
    // 处理数组 - 递归处理数组元素
    return (data as any[]).map(item => convertToUnderscore(item)) as T;
  } else if (type === '[object Object]') {
    // 处理对象 - 只转换属性名，不转换字符串值
    const result = Object.create(null);

    for (const property in data) {
      if (Object.prototype.hasOwnProperty.call(data, property)) {
        const underscoreKey = camelCaseToUnderscore(property);
        const value = (data as any)[property];
        // 对于字符串值，不进行转换；对于其他类型，递归转换
        result[underscoreKey] = typeof value === 'string' ? value : convertToUnderscore(value);
      }
    }

    return result as T;
  } else {
    // 其他类型直接返回
    return data;
  }
}